import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Link,
  Alert,
  Typography,
  Paper,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailIcon from '@mui/icons-material/Email';
import axios from 'axios';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('이메일을 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post('/api/auth/recover-code', { email });

      if (response.data.success) {
        setIsSubmitted(true);
      } else {
        setError(response.data.error || '인증코드 재발급에 실패했습니다.');
      }
    } catch (err) {
      const message = err.response?.data?.error || '인증코드 재발급에 실패했습니다.';
      setError(message);
    }

    setIsLoading(false);
  };

  if (isSubmitted) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          인증코드 재발급 완료
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          새 인증코드가 이메일로 발송되었습니다
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: '#e8f5e9',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutlineIcon sx={{ color: '#2e7d32' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              이메일이 발송되었습니다!
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {email}로 새 인증코드를 보냈습니다.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            이메일을 확인하여 인증코드를 복사한 후 로그인하세요.
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', mb: 3 }}>
          <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <Typography variant="body2" color="text.secondary">
            이메일이 도착하지 않으면 스팸 폴더를 확인해주세요.
          </Typography>
        </Box>

        <Button
          variant="contained"
          fullWidth
          component={RouterLink}
          to="/"
          sx={{
            mb: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
            },
          }}
        >
          메인으로 돌아가기
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        인증코드 찾기
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        가입한 이메일을 입력하시면 새 인증코드를 이메일로 보내드립니다.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="이메일"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
        required
        autoComplete="email"
        autoFocus
        placeholder="work@gmail.com"
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading || !email}
        sx={{
          mt: 3,
          mb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
          },
        }}
      >
        {isLoading ? '처리 중...' : '새 인증코드 받기'}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Link component={RouterLink} to="/" variant="body2">
          메인으로 돌아가기
        </Link>
      </Box>
    </Box>
  );
}
